import axios from 'axios';
import { validTokenExists } from '../../firebaseHelper';
import { UPDATED_SAVED_LIST } from '../types';
import { readSavedPreviewCache, writeSavedPreviewCache, updateSavedPreviewPages } from '../../utils/savedPreviewCache';

let sequence = 0;
const identity = state => ({ uid: state.uid, session: state.session });
const currentAccount = (getState, captured) => {
    const state = getState().savedHighlights;
    return getState().user.authenticated && state.uid === captured.uid && state.session === captured.session;
};
const message = error => error?.response?.data?.error || error?.message || 'Unable to update saved highlight. Please try again.';
const scopedRecord = (record, getState) => record ? { ...record,
    savedListIds: record.savedListIds.filter(id => !getState().savedHighlights.deletedListIds[id]),
} : null;
const cacheMutation = (uid, id, record) => writeSavedPreviewCache(uid,
    updateSavedPreviewPages(readSavedPreviewCache(uid), id, record));

export const hydrateSavedPreviewCache = () => (dispatch, getState) => {
    const state = getState().savedHighlights;
    if (state.uid) dispatch({ type: 'SAVED_HIGHLIGHTS_CACHE', payload: { ...identity(state), pages: readSavedPreviewCache(state.uid) } });
};

// One queue per store/account. It lives outside the drawer so dismissing it never
// cancels a save or lets an older response overwrite newer list choices.
const queues = new WeakMap();
const newRecord = preview => ({ preview, savedAt: new Date().toISOString(), savedListIds: [],
    available: Boolean(preview.mp3URL), fullEpisodeAvailable: true });
const accountQueues = getState => {
    const captured = identity(getState().savedHighlights);
    let account = queues.get(getState);
    if (!account || account.uid !== captured.uid || account.session !== captured.session) {
        account?.entries.clear();
        account = { ...captured, entries: new Map() };
        queues.set(getState, account);
    }
    return account;
};
export const clearStalePreviewMutations = () => (_dispatch, getState) => {
    const account = queues.get(getState);
    if (account && !currentAccount(getState, account)) {
        account.entries.clear();
        queues.delete(getState);
    }
};
const requireAccount = (getState, captured) => {
    if (!currentAccount(getState, captured) || !captured.uid) throw new Error('Your account changed. Please try again.');
};
const queueEntry = (preview, getState) => {
    const account = accountQueues(getState);
    requireAccount(getState, account);
    if (!account.entries.has(preview.id)) {
        const state = getState().savedHighlights;
        const initialRecord = newRecord(preview);
        const confirmed = state.records[preview.id] || (Object.prototype.hasOwnProperty.call(state.memberships, preview.id)
            ? { ...initialRecord, savedListIds: state.memberships[preview.id] } : null);
        account.entries.set(preview.id, { account, preview, initialRecord, confirmed, saved: Boolean(confirmed),
            lists: new Map(), uncertainLists: new Set(), running: false, error: null, uncertainSave: false });
    }
    return account.entries.get(preview.id);
};
const projectedRecord = (entry, getState) => {
    if (entry.error?.kind === 'remove') return scopedRecord(entry.confirmed, getState);
    if (!entry.saved) return null;
    const record = scopedRecord(entry.confirmed || entry.initialRecord, getState);
    const ids = new Set(record.savedListIds);
    entry.lists.forEach((list, key) => {
        if (entry.error?.listKey === key || getState().savedHighlights.deletedListIds[list.listId]) return;
        const id = list.listId || key;
        if (list.present) ids.add(id); else ids.delete(id);
    });
    return { ...record, savedListIds: [...ids] };
};
const publish = (entry, dispatch, getState, finished = false) => {
    requireAccount(getState, entry.account);
    dispatch({ type: 'SAVED_HIGHLIGHTS_MUTATION', payload: { ...identity(entry.account), id: entry.preview.id,
        token: entry.token, record: finished || entry.error ? scopedRecord(entry.confirmed, getState) : projectedRecord(entry, getState),
        mutation: finished ? null : { pending: entry.running, startedAt: entry.startedAt, error: entry.error,
            selectedListIds: projectedRecord(entry, getState)?.savedListIds || [],
            newLists: [...entry.lists.entries()].filter(([, list]) => !list.listId)
                .map(([id, list]) => ({ id, name: list.name, pending: true })) },
    } });
};
const nextWrite = (entry, getState) => {
    // A lost response requires reasserting the latest intent, even when it matches the old snapshot.
    if (entry.uncertainSave || Boolean(entry.confirmed) !== entry.saved) {
        return { kind: entry.saved ? 'save' : 'remove', saved: entry.saved };
    }
    if (!entry.saved) return null;
    for (const [key, list] of entry.lists) {
        if (getState().savedHighlights.deletedListIds[list.listId]) continue;
        if (entry.uncertainLists.has(key) || Boolean(list.listId && entry.confirmed.savedListIds.includes(list.listId)) !== list.present) {
            // An ambiguous creation must first resolve its server id before a
            // newer unchecked choice can remove that membership.
            return { kind: 'list', key, ...list, present: list.listId ? list.present : true };
        }
    }
    return null;
};
const sendWrite = async (entry, write, dispatch, getState) => {
    await validTokenExists();
    requireAccount(getState, entry.account);
    const isList = write.kind === 'list';
    const endpoint = isList ? (write.present ? '/saved_lists_add_preview' : '/saved_lists_delete_preview') : '/save_preview';
    const body = { previewId: entry.preview.id, ...(isList
        ? write.present ? { name: write.name, ...(write.listId ? { listId: write.listId } : {}) } : { listId: write.listId }
        : { saveStatus: write.saved }) };
    if (!isList) entry.uncertainSave = true;
    const { data } = await axios.post(endpoint, body);
    requireAccount(getState, entry.account);
    if ((isList && data?.success !== true) || data?.success === false || data?.error || !data) throw new Error(data?.error || data?.message || 'Unable to update saved highlight.');
    if (!isList && (data.saved !== write.saved || Boolean(data.savedPreview) !== write.saved)) {
        throw new Error('Unable to update saved highlight.');
    }
    if (data.savedPreview && data.savedPreview.preview?.id !== entry.preview.id) throw new Error('Unexpected saved highlight response.');
    if (isList && (typeof data.listId !== 'string' || !data.listId || (write.listId && data.listId !== write.listId))) {
        throw new Error('Unable to confirm the highlight list change.');
    }
    if (isList && write.present && (!data.savedPreview || !data.savedPreview.savedListIds?.includes(data.listId))) {
        throw new Error('Unable to confirm the highlight list change.');
    }
    if (isList && !write.present && data.savedPreview?.savedListIds?.includes(write.listId)) {
        throw new Error('Unable to confirm the highlight list change.');
    }
    if (isList && write.present && data.listId && !getState().savedHighlights.deletedListIds[data.listId]) {
        const intent = entry.lists.get(write.key);
        if (intent) entry.lists.set(write.key, { ...intent, listId: data.listId });
        dispatch({ type: UPDATED_SAVED_LIST, payload: { name: write.name, id: data.listId } });
    }
    entry.confirmed = scopedRecord(data.savedPreview, getState);
    entry.uncertainSave = false;
    if (isList) entry.uncertainLists.delete(write.key);
    // A list response may confirm a remote unsave. Do not silently save it again.
    if (isList && !entry.confirmed) { entry.saved = false; entry.lists.clear(); entry.uncertainLists.clear(); }
    cacheMutation(entry.account.uid, entry.preview.id, entry.confirmed);
};
const drain = (entry, dispatch, getState) => {
    if (entry.running) return entry.promise;
    entry.running = true;
    entry.error = null;
    if (getState().savedHighlights.notice?.id === entry.preview.id) {
        dispatch({ type: 'SAVED_HIGHLIGHTS_NOTICE', payload: { ...identity(entry.account), notice: null } });
    }
    entry.startedAt = Date.now();
    entry.token = ++sequence;
    publish(entry, dispatch, getState);
    entry.promise = (async () => {
        let write;
        try {
            while ((write = nextWrite(entry, getState))) {
                await sendWrite(entry, write, dispatch, getState);
                publish(entry, dispatch, getState);
            }
            entry.running = false;
            publish(entry, dispatch, getState, true);
            entry.account.entries.delete(entry.preview.id);
            return entry.confirmed;
        } catch (error) {
            entry.running = false;
            if (currentAccount(getState, entry.account)) {
                // Removing supersedes any list work, even if that earlier request fails.
                if (!entry.saved && write?.kind !== 'remove') return drain(entry, dispatch, getState);
                const kind = write?.kind === 'list' ? 'list' : entry.saved ? 'save' : 'remove';
                if (kind === 'list') entry.uncertainLists.add(write.key);
                const headline = kind === 'save' ? "Couldn't save highlight." : kind === 'remove'
                    ? "Couldn't remove highlight." : "Couldn't update highlight lists.";
                entry.error = { kind, listKey: write?.key, message: `${headline} ${message(error)}` };
                publish(entry, dispatch, getState);
                dispatch({ type: 'SAVED_HIGHLIGHTS_NOTICE', payload: { ...identity(entry.account),
                    notice: { id: entry.preview.id, key: ++sequence, message: entry.error.message } } });
            }
            throw new Error(message(error));
        }
    })();
    return entry.promise;
};

export const savePreview = (preview, saved = true) => (dispatch, getState) => {
    try {
        const entry = queueEntry(preview, getState);
        entry.saved = saved;
        if (!saved) { entry.lists.clear(); entry.uncertainLists.clear(); }
        if (entry.running) { publish(entry, dispatch, getState); return entry.promise; }
        return drain(entry, dispatch, getState);
    } catch (error) { return Promise.reject(error); }
};

export const updatePreviewList = (preview, { name, listId, remove = false }) => (dispatch, getState) => {
    try {
        const entry = queueEntry(preview, getState);
        if (!entry.saved) throw new Error('Save this highlight before adding it to a list.');
        const existing = [...entry.lists.entries()].find(([, list]) => listId ? list.listId === listId : list.name === name);
        const key = existing?.[0] || listId || `new:${name}`;
        entry.lists.set(key, { name, listId: listId || existing?.[1].listId, present: !remove });
        publish(entry, dispatch, getState);
        if (entry.running) return entry.promise;
        // Only Retry can resume an initial save failure. A new tap on the failed
        // list may retry that choice; unrelated taps remain queued behind it.
        if (entry.error && (entry.error.kind !== 'list' || entry.error.listKey !== key)) {
            return Promise.reject(new Error('Open the saved highlight and tap Retry to finish its pending changes.'));
        }
        return drain(entry, dispatch, getState);
    } catch (error) { return Promise.reject(error); }
};

export const retryPreviewSave = preview => (dispatch, getState) => {
    try { return drain(queueEntry(preview, getState), dispatch, getState); }
    catch (error) { return Promise.reject(error); }
};

export const loadSavedPreviews = (listId = null, cursor = null) => async (dispatch, getState) => {
    const before = getState().savedHighlights;
    const captured = identity(before);
    if (!currentAccount(getState, captured) || !captured.uid) throw new Error('Sign in to view saved highlights.');
    const filter = listId || '__all__';
    const request = ++sequence;
    dispatch({ type: 'SAVED_HIGHLIGHTS_LOADING', payload: { ...captured, filter, request } });
    try {
        await validTokenExists();
        if (!currentAccount(getState, captured)) throw new Error('Your account changed.');
        const { data } = await axios.post('/saved_previews', { ...(listId ? { listId } : {}), ...(cursor ? { cursor } : {}) });
        if (!currentAccount(getState, captured)) throw new Error('Your account changed.');
        if (!Array.isArray(data?.savedPreviews)) throw new Error('Unable to load saved highlights.');
        if (cursor && data.nextCursor && cursor.savedAt === data.nextCursor.savedAt
            && cursor.previewId === data.nextCursor.previewId) {
            throw new Error('Unable to load more saved highlights. Please try again.');
        }
        const records = data.savedPreviews.map(record => scopedRecord(record, getState));
        dispatch({ type: 'SAVED_HIGHLIGHTS_LOADED', payload: { ...captured, filter, request, records,
            cursor, nextCursor: data.nextCursor, revision: before.revision } });
        const state = getState().savedHighlights;
        if (state.requests[filter] !== null) return null;
        // Do not persist optimistic changes from concurrent requests.
        let pages = state.pages;
        const cached = Object.values(readSavedPreviewCache(captured.uid)).flatMap(page => page.records);
        Object.keys(state.mutations).forEach(id => { pages = updateSavedPreviewPages(pages, id, cached.find(record => record.preview.id === id) || null); });
        writeSavedPreviewCache(captured.uid, pages);
        return state.pages[filter];
    } catch (error) {
        dispatch({ type: 'SAVED_HIGHLIGHTS_FAILED', payload: { ...captured, filter, request, cursor, error: message(error) } });
        throw error;
    }
};
